import { MxmPost } from '@/hooks/useMxmPosts';
import { MxmPostCalendar } from '@/hooks/useMxmCalendar';
import { SuggestedItem, CalendarData, CalendarDay, Tag } from '../types';
import { KPI } from '@/types/common';
import { MxmKPIs } from '@/hooks/useMxmKPIs';

/**
 * Transform backend MxmPost to frontend SuggestedItem
 */
export const transformPostToSuggestedItem = (post: MxmPost): SuggestedItem => {
  return {
    id: post.id, // Add id for tracking
    title: post.title,
    subtitle: post.subtitle || '',
    logo: post.logo || null,
    photo: post.photo || null,
    color1: post.color1 || null,
    color2: post.color2 || null,
    color3: post.color3 || null,
    image1: post.image1 || null,
    image2: post.image2 || null,
    renderImageUrl: post.renderImageUrl || null,
    preview: post.subtitle || post.mainText?.substring(0, 100) || '',
    views: post.impressions || 0,
    clicks: post.clicks || 0,
    template: post.contentType || 'unknown',
    forecast: post.scoreHint || 0, // Use scoreHint as forecast
    plannedDate: post.plannedDate || null,
    createdAt: post.createdAt || null,
    status: post.status,
  };
};

/**
 * Map contentType to tag type
 */
const contentTypeToTagType = (contentType?: string): string => {
  if (!contentType) return 'company';

  const lower = contentType.toLowerCase();
  if (lower.includes('news') || lower.includes('commentary')) return 'news';
  if (lower.includes('product') || lower.includes('service')) return 'product';
  if (lower.includes('event') || lower.includes('announce')) return 'event';
  return 'company';
};

/**
 * Transform posts directly to CalendarData format (alternative to calendar entries)
 */
export const transformPostsToCalendarData = (
  posts: MxmPost[],
  monthLabel: string
): CalendarData => {
  // Group posts by date
  const postsByDate = new Map<string, MxmPost[]>();

  posts.forEach((post) => {
    if (!post.plannedDate) return; // Skip posts without plannedDate

    // Extract date string from plannedDate (handle various formats)
    // IMPORTANT: Use local date components, not UTC, because backend stores UTC
    // representing midnight in user's timezone. We need to show the date the user selected.
    let dateStr: string;
    try {
      let date: Date;

      if (typeof post.plannedDate === 'string') {
        if (post.plannedDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // Already in YYYY-MM-DD format - use as-is
          dateStr = post.plannedDate;
        } else {
          // Parse as date
          date = new Date(post.plannedDate);
          if (isNaN(date.getTime())) {
            console.warn('Invalid plannedDate format:', post.plannedDate);
            return; // Skip invalid dates
          }
          // Extract local date components (not UTC) to get the date user selected
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          dateStr = `${year}-${month}-${day}`;
        }
      } else {
        // Date object
        date = new Date(post.plannedDate);
        if (isNaN(date.getTime())) {
          console.warn('Invalid plannedDate:', post.plannedDate);
          return; // Skip invalid dates
        }
        // Extract local date components (not UTC) to get the date user selected
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        dateStr = `${year}-${month}-${day}`;
      }

      if (!postsByDate.has(dateStr)) {
        postsByDate.set(dateStr, []);
      }
      postsByDate.get(dateStr)!.push(post);
    } catch (error) {
      console.warn('Error processing post plannedDate:', post.id, post.plannedDate, error);
    }
  });

  // Create calendar days with tags
  const calendarDays: CalendarDay[] = [];

  // Parse month label (e.g., "January 2025")
  const [monthName, yearStr] = monthLabel.split(' ');
  const year = parseInt(yearStr);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const monthIndex = monthNames.indexOf(monthName);
  if (monthIndex === -1) {
    return {
      monthLabel,
      weeks: [],
    };
  }

  const firstDay = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0);
  const startDay = firstDay.getDay();

  // Add empty days before month starts
  for (let i = 0; i < startDay; i++) {
    calendarDays.push({ day: null });
  }

  // Add days of month
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let day = 1; day <= lastDay.getDate(); day++) {
    const dateStr = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const currentDate = new Date(year, monthIndex, day);
    const isToday = currentDate.getTime() === today.getTime();

    const dayPosts = postsByDate.get(dateStr) || [];
    const tags: Tag[] = dayPosts
      .filter(post => post.status === 'planned' || post.status === 'draft') // Only show planned/draft
      .map((post) => ({
        label: post.title || `Post ${post.id}`,
        type: contentTypeToTagType(post.contentType) as 'news' | 'company' | 'product' | 'event',
        calendarEntryId: post.id, // Use post ID instead of calendar entry ID
        postId: post.id, // Store post ID for reference
        post: post, // Store full post data
      }));

    calendarDays.push({
      day,
      today: isToday,
      tags: tags.length > 0 ? tags : undefined,
    });
  }

  // Split into weeks (7 days each)
  const weeks: CalendarDay[][] = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    weeks.push(calendarDays.slice(i, i + 7));
  }

  return {
    monthLabel,
    weeks,
  };
};

/**
 * Transform calendar entries to CalendarData format
 */
export const transformCalendarToCalendarData = (
  calendarEntries: MxmPostCalendar[],
  monthLabel: string
): CalendarData => {
  // Group entries by date
  const entriesByDate = new Map<string, MxmPostCalendar[]>();

  calendarEntries.forEach((entry) => {
    // Extract date string from plannedDate (handles both "YYYY-MM-DD" and ISO datetime formats)
    let dateStr: string;
    if (typeof entry.plannedDate === 'string') {
      // If already in YYYY-MM-DD format, use it directly
      if (entry.plannedDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        dateStr = entry.plannedDate;
      } else {
        // If it's an ISO datetime, extract date part
        dateStr = entry.plannedDate.split('T')[0];
      }
    } else {
      // If it's a Date object, convert to YYYY-MM-DD
      dateStr = new Date(entry.plannedDate).toISOString().split('T')[0];
    }

    if (!entriesByDate.has(dateStr)) {
      entriesByDate.set(dateStr, []);
    }
    entriesByDate.get(dateStr)!.push(entry);
  });

  // Create calendar days with tags
  const calendarDays: CalendarDay[] = [];

  // Parse month label (e.g., "January 2025")
  const [monthName, yearStr] = monthLabel.split(' ');
  const year = parseInt(yearStr);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const monthIndex = monthNames.indexOf(monthName);
  if (monthIndex === -1) {
    // Fallback to current month if parsing fails
    return {
      monthLabel,
      weeks: [],
    };
  }

  const firstDay = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0);

  // Get first day of week (0 = Sunday)
  const startDay = firstDay.getDay();

  // Add empty days before month starts
  for (let i = 0; i < startDay; i++) {
    calendarDays.push({ day: null });
  }

  // Add days of month
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let day = 1; day <= lastDay.getDate(); day++) {
    // Build date string directly from components to avoid timezone conversion issues
    const dateStr = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    // Create date object for "today" comparison (in local timezone)
    const currentDate = new Date(year, monthIndex, day);
    const isToday = currentDate.getTime() === today.getTime();

    const entries = entriesByDate.get(dateStr) || [];
    const tags: Tag[] = entries.map((entry) => ({
      label: entry.post?.title || `Post ${entry.postId}`,
      type: contentTypeToTagType(entry.post?.contentType),
      calendarEntryId: entry.id,
      calendarEntry: entry, // Store full calendar entry data for drag and drop
    }));

    calendarDays.push({
      day,
      today: isToday,
      tags: tags.length > 0 ? tags : undefined,
    });
  }

  // Split into weeks (7 days each)
  const weeks: CalendarDay[][] = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    weeks.push(calendarDays.slice(i, i + 7));
  }

  return {
    monthLabel,
    weeks,
  };
};

/**
 * Transform KPIs to frontend format
 */
export const transformKPIsToFrontend = (kpis: MxmKPIs): KPI[] => {
  return [
    {
      title: 'Published Content',
      value: kpis.publishedContent || 0,
      subtitle: 'Posts published',
    },
    {
      title: 'Scheduled Content',
      value: kpis.scheduledContent || 0,
      subtitle: 'Posts scheduled',
    },
    {
      title: 'Leads Generated',
      value: kpis.leadsGenerated || 0,
      subtitle: 'Generated leads from posts',
    },
  ];
};

